/**
 * Adding dc.identifier and dc.relation.ispartof meta tags to identify a route uniquely across urls.
 * Used in to make hypothesis annotations work if url parameters change (the part that is before our routes controlled
 * by the player.
 * https://web.hypothes.is/help/how-hypothesis-interacts-with-document-metadata/
 */
import scriptjs from 'scriptjs';
import postMessageService from '../services/PostMessageService';
import sessionReader from '../store/readHelpers/SessionReader';
import storeManager from '../store/StoreManager';
import sessionActions from '../actions/SessionActions';

export const DC_IDENTIFIER = 'dc.identifier';
export const DC_RELATION_ISPARTOF = 'dc.relation.ispartof';

const events = {
    HYPOTHESIS_SIDEBAR_OPENED: 'HYPOTHESIS_SIDEBAR_OPENED',
    HYPOTHESIS_SIDEBAR_CLOSED: 'HYPOTHESIS_SIDEBAR_CLOSED',
};

let hypothesisInitialized = false;

const hypothesisService = {
    getDCIdentifier() {
        if (this.isItemBankContext()) {
            return this.getItemBankDCIdentifier();
        }
        return this.getGradingDCIdentifier();
    },

    getGradingDCIdentifier() {
        const { currentlyShown } = storeManager.getState();
        const assessmentRunId = sessionReader.getAssessmentRunId();
        const questionId = currentlyShown.questionId || sessionReader.getContentRevisionId();
        const partyId = sessionReader.getPartyId();
        if (partyId > 0 && (sessionReader.isContextAnyViewDeliveryType())) {
            return `grading/${assessmentRunId}/${questionId}/${partyId}`;
        }
        return `grading/${assessmentRunId}/${questionId}`;
    },

    getItemBankDCIdentifier() {
        const contentItemId = sessionReader.getContentItemId();
        const contentVersionId = sessionReader.getContentVersionId();

        if (contentVersionId) {
            return `ib/${contentItemId}/${contentVersionId}`;
        }
        return `ib/${contentItemId}`;
    },

    getDCRelationIsPartOf() {
        return 'inspera.com';
    },

    getMetaChildByName(headElement, metaName) {
        return Array.from(headElement.getElementsByTagName('meta')).find((metaElement) => {
            return metaElement.getAttribute('name') === metaName;
        });
    },

    getOrCreateMetaTag(headElement, metaName) {
        const metaTag = this.getMetaChildByName(headElement, metaName);
        if (metaTag) {
            return metaTag;
        }
        const newMetaTag = document.createElement('meta');
        newMetaTag.setAttribute('name', metaName);
        headElement.appendChild(newMetaTag);
        return newMetaTag;
    },

    addDCIdentifierContentsToDom(headElement, dcIdentifier, dcRelationIsPartOf) {
        const dcIdentifierElement = this.getOrCreateMetaTag(headElement, DC_IDENTIFIER);
        const dcRelationElement = this.getOrCreateMetaTag(headElement, DC_RELATION_ISPARTOF);
        dcIdentifierElement.setAttribute('content', dcIdentifier);
        dcRelationElement.setAttribute('content', dcRelationIsPartOf);
    },

    hasMetaTag(headElement, metaName) {
        return this.getMetaChildByName(headElement, metaName);
    },

    resetDCIdentifier(headElement) {
        const dcIdentifierElement = this.getOrCreateMetaTag(headElement, DC_IDENTIFIER);
        const dcRelationElement = this.getOrCreateMetaTag(headElement, DC_RELATION_ISPARTOF);
        dcIdentifierElement.setAttribute('content', '');
        dcRelationElement.setAttribute('content', '');
    },

    initializeHypothesis() {
        const state = storeManager.getState();
        const annotationSidebarDefaultExpanded = state.session.annotationSidebarDefaultExpanded;
        hypothesisInitialized = true;

        window.addEventListener(events.HYPOTHESIS_SIDEBAR_OPENED, this.handleSidebarOpened);
        window.addEventListener(events.HYPOTHESIS_SIDEBAR_CLOSED, this.handleSidebarClosed);

        scriptjs(`${window.hypothesisClientRoot}build/boot.js`,
            () => this.appendSidebarExpandedSetting(annotationSidebarDefaultExpanded));
    },

    appendSidebarExpandedSetting(annotationSidebarDefaultExpanded) {
        const headElement = document.head || document.getElementsByTagName('head')[0];
        const newMetaTag = document.createElement('meta');
        newMetaTag.setAttribute('name', 'annotationSidebarDefaultExpanded');
        newMetaTag.setAttribute('content', annotationSidebarDefaultExpanded);
        headElement.appendChild(newMetaTag);
    },

    handleSidebarOpened() {
        document.getElementById('wrapper').classList.add('hypothesis-active');
        const store = storeManager.getStore();
        store.dispatch(sessionActions.setHypothesisSidebarOpen(true));
        postMessageService.postTo(window.parent, { type: 'player:hypothesis-sidebar-state-change', payload: { isOpen: true } });
    },

    handleSidebarClosed() {
        document.getElementById('wrapper').classList.remove('hypothesis-active');
        const store = storeManager.getStore();
        store.dispatch(sessionActions.setHypothesisSidebarOpen(false));
        postMessageService.postTo(window.parent, { type: 'player:hypothesis-sidebar-state-change', payload: { isOpen: false } });
    },

    isItemBankContext() {
        return sessionReader.getAnnotationContext() === 'ib';
    },

    isGradingContext() {
        return sessionReader.getAnnotationContext() === 'grading';
    },

    checkIfResetDCIIdentifierIsNeeded() {
        const headElement = document.head || document.getElementsByTagName('head')[0];
        const metaTagExists = this.hasMetaTag(headElement, DC_IDENTIFIER) && this.hasMetaTag(headElement, DC_RELATION_ISPARTOF);
        if (metaTagExists) {
            if (this.isItemBankContext()) {
                const contentItemId = sessionReader.getContentItemId();
                if (contentItemId < 0) {
                    this.resetDCIdentifier(headElement);
                }
            } else {
                const { currentlyShown } = storeManager.getState();
                const assessmentRunId = sessionReader.getAssessmentRunId();
                const questionId = currentlyShown.questionId;
                if (!(assessmentRunId > 0 && questionId > 0)) {
                    this.resetDCIdentifier(headElement);
                }
            }
        }
    },

    appendDCMIMetaDataTagsAndInitHypothesisIfNeeded() {
        const headElement = document.head || document.getElementsByTagName('head')[0];
        const dcIdentifier = this.getDCIdentifier();
        const dcRelationIsPartOf = this.getDCRelationIsPartOf();
        this.checkIfResetDCIIdentifierIsNeeded();
        this.addDCIdentifierContentsToDom(
            headElement,
            dcIdentifier,
            dcRelationIsPartOf,
        );

        if (!hypothesisInitialized) {
            this.initializeHypothesis();
        }
    },
};

export default hypothesisService;
